import {
  hasInFlightMockupJob,
  persistPendingFeedback,
  readPendingFeedback,
  resumeMockupGenerationForItem,
  type MockupFileEntity,
} from "../../services/mockupGeneration";
import {
  executeMockupGeneration,
  MockupStatus,
  validateMockupInputs,
  type HandleMockupGenerationParams,
  type MockupGenerationCallbacks,
  type MockupGenerationContext,
  type MockupGenerationResult,
  type MockupStatusState,
} from "./mockupUtils";

const DEFAULT_PROCESSING_MESSAGE = "Configuring products...";

export function getGeneratingMockupStatus(message: string = DEFAULT_PROCESSING_MESSAGE): MockupStatusState {
  return { type: MockupStatus.PROCESSING, message };
}

export function getMockupPreflightError(args: {
  params: HandleMockupGenerationParams;
  itemId: string | null | undefined;
}): string | null {
  const { params, itemId } = args;
  const validationError = validateMockupInputs(params);
  if (validationError) return validationError;
  if (!itemId) return "Unable to configure a product: missing product information.";
  return null;
}

function statusFromResult(result: MockupGenerationResult): MockupStatusState {
  if (!result.success) {
    return { type: MockupStatus.ERROR, message: ("errorMessage" in result ? result.errorMessage : "An error occurred while generating the mockup.") };
  }

  if (!result.file) {
    return { type: MockupStatus.IDLE, message: null };
  }

  const jobId = 'jobId' in result ? result.jobId : null;

  if (result.warnings.length) {
    return { type: MockupStatus.WARNING, message: result.warnings.join(" "), jobId };
  }

  return { type: MockupStatus.SUCCESS, message: null, jobId };
}

export async function runMockupGenerationFlow(args: {
  params: HandleMockupGenerationParams;
  context: Omit<MockupGenerationContext, "itemId"> & { itemId: string | null | undefined };
  callbacks: MockupGenerationCallbacks;
}): Promise<MockupStatusState> {
  const { params, context, callbacks } = args;

  const preflightError = getMockupPreflightError({ params, itemId: context.itemId });
  if (preflightError) {
    return { type: MockupStatus.ERROR, message: preflightError };
  }

  const result = await executeMockupGeneration(
    params,
    {
      ...context,
      itemId: context.itemId as string,
    },
    callbacks
  );

  return statusFromResult(result);
}

interface ResumeMockupCallbacks {
  onStatusChange: (status: MockupStatusState) => void;
  onFileReady: (file: MockupFileEntity) => void;
}

export async function resumeMockupGeneration(
  itemId: string | undefined,
  callbacks: ResumeMockupCallbacks,
): Promise<void> {
  if (!itemId || !hasInFlightMockupJob(itemId)) return;

  callbacks.onStatusChange({ type: MockupStatus.PROCESSING, message: DEFAULT_PROCESSING_MESSAGE });

  try {
    const resumed = await resumeMockupGenerationForItem({ itemId });
    if (!resumed?.file) {
      callbacks.onStatusChange({ type: MockupStatus.IDLE, message: null });
      return;
    }
    callbacks.onFileReady(resumed.file);
    const jobId = resumed.job_id ?? null;
    if (jobId) persistPendingFeedback(itemId, jobId);
    callbacks.onStatusChange({ type: MockupStatus.SUCCESS, message: null, jobId });
  } catch (e) {
    const errorMessage =
      (e as Error)?.message || "Failed to resume product configuration. Please try again.";
    callbacks.onStatusChange({ type: MockupStatus.ERROR, message: errorMessage });
  }
}

export function getPendingFeedbackStatus(itemId: string | undefined): MockupStatusState | null {
  if (!itemId) return null;
  const jobId = readPendingFeedback(itemId);
  if (!jobId) return null;
  return { type: MockupStatus.SUCCESS, message: null, jobId };
}

